import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { QuoteForm } from "@/components/QuoteForm";
import {
  LanguageMarquee, ComplexCta, TrustStrip, WhyUs, Services, Industries,
  HowItWorks, FinalCta,
} from "@/components/home/Sections";

const lineReveal = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: 0,
    transition: { delay: 0.12 * i + 0.15, duration: 0.85, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const globeY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const gridY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <div data-testid="home-page" className="bg-[#FAF8F5]">
      <Header />

      <section ref={heroRef} data-testid="hero" className="relative overflow-hidden pt-[72px]">
        <motion.div style={{ y: gridY }} className="absolute inset-0 hero-grid-bg" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FAF8F5]/40 via-transparent to-[#F1ECE4]" />
        <motion.div
          style={{ y: globeY }}
          className="absolute -right-32 top-24 w-[420px] h-[420px] rounded-full border border-[#C87A4B]/20 hidden lg:block"
        >
          <div className="absolute inset-8 rounded-full border border-[#C87A4B]/15" />
          <div className="absolute inset-20 rounded-full border border-[#C87A4B]/10" />
          <div className="absolute top-1/2 left-0 right-0 h-px bg-[#C87A4B]/15" />
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-[#C87A4B]/15" />
        </motion.div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-14 pb-20 lg:pt-20 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-start">
          <div className="lg:col-span-6 lg:pt-8">
            <motion.p
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="text-xs font-mono uppercase tracking-[0.2em] text-[#C87A4B] mb-6"
            >
              Global Translation Platform
            </motion.p>
            <h1 className="font-heading font-bold tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-6xl text-[#0B111E]">
              <span className="block overflow-hidden pb-1">
                <motion.span className="block" variants={lineReveal} initial="hidden" animate="visible" custom={0}>
                  Professional
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-1">
                <motion.span className="block" variants={lineReveal} initial="hidden" animate="visible" custom={1}>
                  Translation,
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-2">
                <motion.span className="block text-[#C87A4B]" variants={lineReveal} initial="hidden" animate="visible" custom={2}>
                  Made Simple.
                </motion.span>
              </span>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.65, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="mt-6 text-base sm:text-lg leading-relaxed text-slate-600 max-w-lg"
            >
              Accurate, professional translation solutions for businesses and individuals across global markets.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.7 }}
              className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-mono uppercase tracking-[0.15em] text-slate-500"
            >
              <span>30+ Languages</span>
              <span className="text-[#C87A4B]">◆</span>
              <span>Human Reviewed</span>
              <span className="text-[#C87A4B]">◆</span>
              <span>Secure Upload</span>
            </motion.div>
          </div>

          <div id="quote" className="lg:col-span-6 scroll-mt-24">
            <QuoteForm />
          </div>
        </div>
      </section>

      <LanguageMarquee />
      <ComplexCta />
      <TrustStrip />
      <WhyUs />
      <Services />
      <Industries />
      <HowItWorks />
      <FinalCta />
      <Footer />
    </div>
  );
}
